import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { CompareProvider } from "@/hooks/useCompare";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { ThemeProvider } from "next-themes";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { CompareBar } from "@/components/compare/CompareBar";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AIChatAssistant } from "@/components/chat/AIChatAssistant";
import { CategoryQueryRedirect, RedirectTo } from "@/components/Redirect";

// Lazy load all non-critical routes to reduce initial bundle size
const Search = lazy(() => import("./pages/Search"));
const Skills = lazy(() => import("./pages/Skills"));
const SkillDetail = lazy(() => import("./pages/SkillDetail"));
const SubmitSkill = lazy(() => import("./pages/SubmitSkill"));
const ToolDetail = lazy(() => import("./pages/ToolDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const Favorites = lazy(() => import("./pages/Favorites"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Compare = lazy(() => import("./pages/Compare"));
const SharedCompare = lazy(() => import("./pages/SharedCompare"));
const Categories = lazy(() => import("./pages/Categories"));
const CategoryDetail = lazy(() => import("./pages/CategoryDetail"));
const Alternatives = lazy(() => import("./pages/Alternatives"));
const SubmitTool = lazy(() => import("./pages/SubmitTool"));
const Admin = lazy(() => import("./pages/Admin"));
const GenerateEbook = lazy(() => import("./pages/admin/GenerateEbook"));
const CarouselBuilder = lazy(() => import("./pages/CarouselBuilder"));
const CarouselTemplates = lazy(() => import("./pages/CarouselTemplates"));
const SharedCarouselView = lazy(() => import("./pages/SharedCarouselView"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const ToolBattles = lazy(() => import("./pages/ToolBattles"));
const IncomeBlueprints = lazy(() => import("./pages/IncomeBlueprints"));
const IncomeBlueprintDetail = lazy(() => import("./pages/IncomeBlueprintDetail"));
const IncomeReports = lazy(() => import("./pages/IncomeReports"));
const IncomeReportDetail = lazy(() => import("./pages/IncomeReportDetail"));
const WallOfFame = lazy(() => import("./pages/WallOfFame"));
const ToolHealth = lazy(() => import("./pages/ToolHealth"));
const News = lazy(() => import("./pages/News"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Templates = lazy(() => import("./pages/Templates"));
const SellerEarnings = lazy(() => import("./pages/SellerEarnings"));
const StackBuilder = lazy(() => import("./pages/StackBuilder"));
const VibeCodingCompare = lazy(() => import("./pages/VibeCodingCompare"));
const MobileFrameworkCompare = lazy(() => import("./pages/MobileFrameworkCompare"));
const PromptsLibrary = lazy(() => import("./pages/PromptsLibrary"));
const ReferralLeaderboard = lazy(() => import("./pages/ReferralLeaderboard"));
const Challenges = lazy(() => import("./pages/Challenges"));
const ChallengeDetail = lazy(() => import("./pages/ChallengeDetail"));
const ChallengeLeaderboard = lazy(() => import("./pages/ChallengeLeaderboard"));
const VibeQuiz = lazy(() => import("./pages/VibeQuiz"));
const AILearningGuide = lazy(() => import("./pages/AILearningGuide"));
const MyLearning = lazy(() => import("./pages/MyLearning"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const VibeCodingMarketplace = lazy(() => import("./pages/VibeCodingMarketplace"));
const VibeCoderProfile = lazy(() => import("./pages/VibeCoderProfile"));
const BecomeVibeCoder = lazy(() => import("./pages/BecomeVibeCoder"));
const MarketplaceMessages = lazy(() => import("./pages/MarketplaceMessages"));
const PostProject = lazy(() => import("./pages/PostProject"));
const CoderDashboard = lazy(() => import("./pages/CoderDashboard"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const SocialCards = lazy(() => import("./pages/SocialCards"));
const Tools = lazy(() => import("./pages/Tools"));
const MiddleEastAI = lazy(() => import("./pages/MiddleEastAI"));
const BestFreeAITools = lazy(() => import("./pages/BestFreeAITools"));
const MyTools = lazy(() => import("./pages/MyTools"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DigitalProducts = lazy(() => import("./pages/DigitalProducts"));
const AIToolsPlaybook = lazy(() => import("./pages/AIToolsPlaybook"));
const DigitalProductDetail = lazy(() => import("./pages/DigitalProductDetail"));
const SellDigitalProduct = lazy(() => import("./pages/SellDigitalProduct"));
const SellerTerms = lazy(() => import("./pages/SellerTerms"));
const BuyerTerms = lazy(() => import("./pages/BuyerTerms"));
const CreatorTerms = lazy(() => import("./pages/CreatorTerms"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const CancellationPolicy = lazy(() => import("./pages/CancellationPolicy"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const MyPurchases = lazy(() => import("./pages/MyPurchases"));
const MyStack = lazy(() => import("./pages/MyStack"));
const Monetization = lazy(() => import("./pages/Monetization"));
const PromptPacks = lazy(() => import("./pages/PromptPacks"));
const ToolDeals = lazy(() => import("./pages/ToolDeals"));
const ToolDealDetail = lazy(() => import("./pages/ToolDealDetail"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const PostJob = lazy(() => import("./pages/PostJob"));
const MyApplications = lazy(() => import("./pages/MyApplications"));
const Events = lazy(() => import("./pages/Events"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventsCalendar = lazy(() => import("./pages/EventsCalendar"));
const EventsPricing = lazy(() => import("./pages/EventsPricing"));
const EventSubmit = lazy(() => import("./pages/EventSubmit"));
const Sitemap = lazy(() => import("./pages/Sitemap"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Legal = lazy(() => import("./pages/Legal"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Credits = lazy(() => import("./pages/Credits"));
const LandingBuilder = lazy(() => import("./pages/LandingBuilder"));
const LandingTemplates = lazy(() => import("./pages/LandingTemplates"));
const PublishedLandingPage = lazy(() => import("./pages/PublishedLandingPage"));
const RoastArena = lazy(() => import("./pages/RoastArena"));
const WhyJoin = lazy(() => import("./pages/WhyJoin"));
const InfographicBuilder = lazy(() => import("./pages/InfographicBuilder"));
const queryClient = new QueryClient();

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <CurrencyProvider>
            <AuthProvider>
              <CompareProvider>
                <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <ScrollToTop />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<CategoryQueryRedirect><Index /></CategoryQueryRedirect>} />
                      <Route path="/tools" element={<Tools />} />
                      <Route path="/skills" element={<Skills />} />
                      <Route path="/skills/submit" element={<SubmitSkill />} />
                      <Route path="/skills/:slug" element={<SkillDetail />} />
                      <Route path="/best-free-ai-tools" element={<BestFreeAITools />} />
                      <Route path="/middle-east" element={<MiddleEastAI />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/tool/:slug" element={<ToolDetail />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/categories/:slug" element={<CategoryDetail />} />
                      {/* Legacy redirects */}
                      <Route path="/vibe-coding-marketplace" element={<RedirectTo to="/marketplace" />} />
                      <Route path="/tool-battles" element={<RedirectTo to="/compare" />} />
                      <Route path="/favorites" element={<Favorites />} />
                      <Route path="/submit" element={<SubmitTool />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path="/admin/generate-ebook" element={<GenerateEbook />} />
                      <Route path="/compare" element={<Compare />} />
                      <Route path="/compare/shared/:shareCode" element={<SharedCompare />} />
                      <Route path="/compare/vibe-coding" element={<VibeCodingCompare />} />
                      <Route path="/vibe-compare" element={<VibeCodingCompare />} />
                      <Route path="/compare/mobile-frameworks" element={<MobileFrameworkCompare />} />
                      <Route path="/quiz/vibe-coding" element={<VibeQuiz />} />
                      <Route path="/leaderboard" element={<Leaderboard />} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/stack-builder" element={<StackBuilder />} />
                      <Route path="/health" element={<ToolHealth />} />
                      <Route path="/status" element={<ToolHealth />} />
                      <Route path="/battles" element={<ToolBattles />} />
                      <Route path="/portfolio" element={<Portfolio />} />
                      <Route path="/alternatives" element={<Alternatives />} />
                      <Route path="/prompts" element={<PromptsLibrary />} />
                      <Route path="/templates" element={<Templates />} />
                      <Route path="/seller-earnings" element={<SellerEarnings />} />
                      <Route path="/carousel-builder" element={<CarouselBuilder />} />
                      <Route path="/carousel-templates" element={<CarouselTemplates />} />
                      <Route path="/carousel/:shareCode" element={<SharedCarouselView />} />
                      <Route path="/social-cards" element={<SocialCards />} />
                      <Route path="/referrals" element={<ReferralLeaderboard />} />
                      <Route path="/news" element={<News />} />
                      <Route path="/news/:slug" element={<NewsArticle />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route path="/blog/:slug" element={<BlogPost />} />
                      <Route path="/wall-of-fame" element={<WallOfFame />} />
                      <Route path="/ai-learning" element={<AILearningGuide />} />
                      <Route path="/my-learning" element={<MyLearning />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/my-tools" element={<MyTools />} />
                      <Route path="/income-blueprints" element={<IncomeBlueprints />} />
                      <Route path="/income-blueprints/:slug" element={<IncomeBlueprintDetail />} />
                      <Route path="/challenges" element={<Challenges />} />
                      <Route path="/challenges/:id" element={<ChallengeDetail />} />
                      <Route path="/challenges/leaderboard" element={<ChallengeLeaderboard />} />
                      <Route path="/income-reports" element={<IncomeReports />} />
                      <Route path="/income-reports/:id" element={<IncomeReportDetail />} />
                      <Route path="/marketplace" element={<VibeCodingMarketplace />} />
                      <Route path="/marketplace/coder/:coderId" element={<VibeCoderProfile />} />
                      <Route path="/marketplace/become-coder" element={<BecomeVibeCoder />} />
                      <Route path="/marketplace/messages" element={<MarketplaceMessages />} />
                      <Route path="/marketplace/messages/:conversationId" element={<MarketplaceMessages />} />
                      <Route path="/marketplace/post-project" element={<PostProject />} />
                      <Route path="/marketplace/dashboard" element={<Dashboard />} />
                      <Route path="/marketplace/project/:projectId" element={<ProjectDetail />} />
                      <Route path="/digital-products" element={<DigitalProducts />} />
                      <Route path="/ai-tools-playbook" element={<AIToolsPlaybook />} />
                      <Route path="/digital-products/:slug" element={<DigitalProductDetail />} />
                      <Route path="/sell-digital-product" element={<SellDigitalProduct />} />
                      <Route path="/seller-terms" element={<SellerTerms />} />
                      <Route path="/buyer-terms" element={<BuyerTerms />} />
                      <Route path="/creator-terms" element={<CreatorTerms />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/help" element={<HelpCenter />} />
                      <Route path="/help-center" element={<HelpCenter />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/refund-policy" element={<RefundPolicy />} />
                      <Route path="/cancellation-policy" element={<CancellationPolicy />} />
                      <Route path="/payment/success" element={<PaymentSuccess />} />
                      <Route path="/payment/cancel" element={<PaymentCancel />} />
                      <Route path="/my-purchases" element={<MyPurchases />} />
                      <Route path="/my-stack" element={<MyStack />} />
                      <Route path="/premium" element={<Monetization />} />
                      <Route path="/prompt-packs" element={<PromptPacks />} />
                      <Route path="/tool-deals" element={<ToolDeals />} />
                      <Route path="/tool-deals/:slug" element={<ToolDealDetail />} />
                      <Route path="/jobs" element={<Jobs />} />
                      <Route path="/jobs/:slug" element={<JobDetail />} />
                      <Route path="/post-job" element={<PostJob />} />
                      <Route path="/my-applications" element={<MyApplications />} />
                      <Route path="/events" element={<Events />} />
                      <Route path="/events/calendar" element={<EventsCalendar />} />
                      <Route path="/events/pricing" element={<EventsPricing />} />
                      <Route path="/events/submit" element={<EventSubmit />} />
                      <Route path="/events/:slug" element={<EventDetail />} />
                      <Route path="/sitemap" element={<Sitemap />} />
                      <Route path="/pricing" element={<Pricing />} />
                      <Route path="/legal" element={<Legal />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/credits" element={<Credits />} />
                      <Route path="/landing-builder/new" element={<LandingBuilder />} />
                      <Route path="/landing-builder/:id" element={<LandingBuilder />} />
                      <Route path="/landing-templates" element={<LandingTemplates />} />
                      <Route path="/p/:slug" element={<PublishedLandingPage />} />
                      <Route path="/roast-arena" element={<RoastArena />} />
                      <Route path="/join" element={<WhyJoin />} />
                      <Route path="/why-join" element={<WhyJoin />} />
                      <Route path="/infographic-builder" element={<InfographicBuilder />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                    <CompareBar />
                    <AIChatAssistant />
                  </BrowserRouter>
                </TooltipProvider>
              </CompareProvider>
            </AuthProvider>
          </CurrencyProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;